import { inject, Injectable } from '@angular/core';
import { map, Observable, withLatestFrom } from 'rxjs';
import { GenWeatherPhenom } from '../generated/serverModels/GenWeatherPhenom';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Territory } from '../core/classes/territory';
import { GenWeatherBaseline } from '../generated/serverModels/GenWeatherBaseline';
import { Store } from '@ngrx/store';
import { selectCounties } from '../state/location/location.reducer';
import { County } from '../core/classes/county';
import { RestorationMetric } from '../shared/classes/restoration-metric';
import { WeatherPhenomGroup } from '../shared/classes/weather/weather-phenom-group';
import { AppService } from './app.service';

@Injectable({
  providedIn: 'root',
})
export class WeatherBaselineService {
  private readonly baseUrl: string = 'api/weather';

  private store = inject(Store);
  private http = inject(HttpClient);
  private appService = inject(AppService);

  constructor() {
  }


  public getWeatherPhenom(): Observable<GenWeatherPhenom[]> {
    const url = `${this.baseUrl}/phenom`;
    return this.http.get<GenWeatherPhenom[]>(url).pipe(
      map(phenoms => {
        return phenoms.map(phenom => new GenWeatherPhenom(phenom))
          .sort((a, b) => {
            if (a.description > b.description) return 1;
            else if (a.description < b.description) return -1;
            return 0;
          });
      }),
    );
  }

  public getWeatherPhenomGroups(): Observable<WeatherPhenomGroup[]> {
    const url = `${this.baseUrl}/phenom-group`;
    return this.http.get<WeatherPhenomGroup[]>(url).pipe(
      map(groups => {
        return groups.map(group => new WeatherPhenomGroup(group));
      }),
    );
  }


  public getWeatherBaselines(territories: Territory[],
    phenoms: GenWeatherPhenom[],
    years: number[], onlyExtremeEvents: boolean = false) {
    const url = `${this.baseUrl}/filterByYear`;
    let params = new HttpParams();

    params = params.append('year', years.join());
    params = params.append('phenom', phenoms.map(phenom => phenom.abbv).join());
    params = params.append('state', territories.map(territory => territory.abbreviation).join());
    params = params.append('extreme', onlyExtremeEvents);




    return this.http.get<GenWeatherBaseline[]>(url, { params }).pipe(
      map(weatherBaselines => {
        return weatherBaselines.reduce((prev, cur) => {
          const val = prev.get(cur.fipsCode) || [];
          val.push(cur);
          prev.set(cur.fipsCode, val);
          return prev;
        }, new Map<string, GenWeatherBaseline[]>());
      }),
      withLatestFrom(this.store.select(selectCounties)),
      map((res: [Map<string, GenWeatherBaseline[]>, County[]]) => {
        const features: {type: string; geometry: unknown; properties: {fipsCode: string; events: GenWeatherBaseline[]; county: string; state: string}}[] = [];
        let min = Number.MAX_SAFE_INTEGER;
        let max = Number.MIN_SAFE_INTEGER;


        res[0].forEach((value, key) => {
          const county = res[1].find(county => county.geoid === key)!;

          min = Math.min(min, value.length);
          max = Math.max(max, value.length);

          features.push({
            type: 'Feature',
            geometry: JSON.parse(county.boundary),
            properties: {
              fipsCode: key,
              events: value,
              county: county.name,
              state: county.stateAbbreviation,
            },
          });
        });

        return {
          type: 'FeatureCollection',
          features,
          min,
          max,
        };
      }),
    );
  }


  public getRestorationMetricsForWeatherEvent(eventId: number): Observable<RestorationMetric[]> {
    const url = `api/metrics/weather/${eventId}`;
    return this.http.get<RestorationMetric[]>(url).pipe(
      map(res => res.map(metric => new RestorationMetric(metric))),
    );
  }
}
